import type { MachineAuthority } from '../types/workflow.js';

/**
 * THE read rule for actor provenance, mirroring `actor-validator.ts` on the API side: only an
 * explicit `'machine'` is a machine actor, and absence means NOT DECLARED MACHINE — weaker than
 * "human", because an authenticated session proves who submitted a request, not that a person read
 * the thing. Render absence as NOTHING, never as an "unknown" badge: every row predating the field
 * has none, and badging all of them is noise that teaches readers to ignore the badge that matters.
 * Never infer machine-ness from anything else (an `e2e:`-prefixed reviewerId, an address shape).
 */
export function isMachineActor(row: { actorType?: string | undefined } | null | undefined): boolean {
  return row?.actorType === 'machine';
}

/** One line naming the authority, for a `title` tooltip on surfaces too dense to spell it out. */
export function machineAuthorityTitle(authority?: MachineAuthority | undefined): string {
  if (!authority) {
    return 'Automated action — no authority recorded';
  }
  const note = authority.note ? ` — ${authority.note}` : '';
  return `Automated action under decision ${authority.decisionId} · batch ${authority.batchId}${note}`;
}

/**
 * Marks a decision as machine-made. A bulk approve runs under the operator's session identity, so
 * without this the screen asserts that a named colleague read every item — which is the one thing
 * the review trail must never say falsely.
 */
export function MachineActorBadge({
  authority,
  testId,
}: {
  authority?: MachineAuthority | undefined;
  testId: string;
}) {
  return (
    <span
      className="inline-flex items-center gap-1 rounded bg-amber-100 px-1 py-px font-semibold uppercase tracking-wide text-amber-800"
      data-testid={testId}
      title={machineAuthorityTitle(authority)}
    >
      <span aria-hidden="true" className="material-symbols-outlined text-[11px] leading-none">
        smart_toy
      </span>
      Automated
    </span>
  );
}

/** The authority spelled out in full, for surfaces with room for a line of explanation. */
export function MachineAuthorityLine({ authority }: { authority?: MachineAuthority | undefined }) {
  if (!authority) {
    return null;
  }
  return (
    <p className="mt-0.5 text-[10px] leading-relaxed text-slate-500">
      Authorised by <span className="font-semibold text-slate-600">{authority.decisionId}</span>
      {' · batch '}
      <span className="font-mono">{authority.batchId}</span>
      {authority.note ? (
        <>
          {' — '}
          <span>{authority.note}</span>
        </>
      ) : null}
    </p>
  );
}
